import { useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiSolidMinusCircle, BiSolidPlusCircle } from 'react-icons/bi';

import CopyVariable from '@/components/common/CopyVariable';
import PopoverWithAudio from '@/components/common/PopoverWithAudio';
import { PSButton } from '@/components/ui/button';

export default function ResponseMapper({
  register,
  formState: { errors },
  control,
  watch,
}: {
  register: UseFormRegister<any>;
  formState: { errors: FieldErrors<any> };
  control: Control<any>;
  watch: UseFormWatch<any>;
}) {
  const { t } = useTranslation();
  const [showMapGuide, setShowMapGuide] = useState(false);
  const {
    fields: responseParserFields,
    append: responseParserAppend,
    remove: responseParserRemove,
  } = useFieldArray({
    control,
    name: 'responseParser',
  });

  return (
    <>
      <div className="h-[0px] border border-gray-200"></div>
      <Row>
        <Col xs={12}>
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
              <div className="tw-flex tw-flex-inline tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('McBotHttpForm/key14')}
                <PopoverWithAudio
                  data={{
                    contentText: t('McBotHttpForm/key21'),
                    size: 15,
                  }}
                />
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-3">
              <PSButton id="btn-show-map-guide" variant="outline" type="button" onClick={() => setShowMapGuide(true)} title={t('McBotHttpForm/key24')} />
            </div>

            {responseParserFields.map((field, index) => (
              <Row key={field.id} className={`tw-mt-2 tw-items-start tw-mr-3`}>
                <Col md={10} xs={10}>
                  <Row className="gap-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Controller
                          name={`responseParser.${index}.key`}
                          control={control}
                          render={() => <CopyVariable title={watch('title')} text={watch(`responseParser.${index}.newValue`)} noTitle />}
                        />

                        <Form.Control.Feedback type="invalid" className="tw-block">
                          {errors.responseParser?.[index]?.key?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Control
                          className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                          type="text"
                          placeholder={t('McBotHttpForm/key7').toString()}
                          isInvalid={!!errors.responseParser?.[index]?.newValue}
                          {...register(`responseParser.${index}.newValue`)}
                        />
                        <Form.Control.Feedback type="invalid" className="tw-block">
                          {errors.responseParser?.[index]?.newValue?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>

                <Col md={1} xs={2} className="tw-self-stretch">
                  <PSButton
                    id="btn-response-parse-remove"
                    variant="danger"
                    type="button"
                    onClick={() => responseParserRemove(index)}
                    icon={<BiSolidMinusCircle size={20} />}
                  />
                </Col>
              </Row>
            ))}

            <PSButton
              id="btn-response-parse-append"
              variant="outline"
              type="button"
              onClick={() => {
                responseParserAppend({
                  newValue: '',
                  key: '',
                  oldValue: null,
                });
              }}
              icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
              title={t('McBotHttpForm/key25')}
            />
          </div>
        </Col>
      </Row>
      <Modal show={showMapGuide} onHide={() => setShowMapGuide(false)} size="lg" centered>
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-4">
                <span className="tw-text-gray-400 tw-flex tw-flex-inline tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                  {t('McBotHttpForm/key11')}
                  <PopoverWithAudio
                    data={{
                      contentText: t('McBotHttpForm/key20'),
                      size: 20,
                    }}
                  />
                </span>
              </div>

              <div className="tw-min-h-[217px] tw-justify-start tw-items-start tw-gap-2 tw-grid tw-grid-cols-12 tw-w-full">
                <div className="md:tw-col-span-4 tw-col-span-12 tw-flex tw-flex-col tw-h-full">
                  <div className="tw-overflow-auto tw-h-full tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-gray-200 tw-rounded-md tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                    <div className="tw-self-stretch justify-between tw-items-center tw-inline-flex">
                      <div className="tw-w-[219px] tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t('McBotHttpForm/key12')}
                      </div>
                      <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      <pre>
                        {JSON.stringify(
                          {
                            users: [{ name: 'john', email: 'john@mail.com' }],
                          },
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
                <div className="md:tw-col-span-8 tw-col-span-12 tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start items-center tw-gap-4 tw-flex">
                  <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start tw-items-start tw-gap-4 tw-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                      <div className="tw-self-stretch tw-h-[217px] tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                        <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                          <div className="tw-text-gray-500 tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t('McBotHttpForm/key13')}
                            <br />
                            users.0.email
                          </div>
                          <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
}
